import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Panel, PanelHeader, PanelBody } from '../ui/Panel';
import { SkeletonRows, ErrorNotice, InfoNotice } from '../ui/States';
import { fmt } from '../../lib/format';
import { label } from '../../lib/programs';
import { useCanSee } from '../../contexts/AuthContext';
import type { EventLedger, PanelProps } from './types';

const STATUSES = ['owed', 'requested', 'submitted', 'approved', 'paid', 'cleared', 'cancelled'];
const money = (cents: number | null) =>
  cents === null ? '—' : (cents / 100).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

/** The event's ledger entries of some kinds: statuses for everyone, people and amounts for admins. */
export default function LedgerPanel({
  detail,
  kinds,
  title,
  lead,
}: PanelProps & { kinds: string[]; title: string; lead: string }) {
  const see = useCanSee();
  const q = useQuery<EventLedger>(
    ['event-ledger', detail.event.id],
    async () => (await api.get(`/events/${detail.event.id}/ledger`)).data.data,
  );
  if (q.isLoading) return <SkeletonRows rows={4} />;
  if (q.error || !q.data) return <ErrorNotice>Could not load the ledger.</ErrorNotice>;
  const counts: Record<string, number> = {};
  for (const k of kinds) for (const [s, n] of Object.entries(q.data.counts[k] ?? {})) counts[s] = (counts[s] ?? 0) + n;
  const entries = q.data.entries.filter((e) => kinds.includes(e.kind));
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <Panel>
      <PanelHeader title={title} note={`${fmt(total)} entr${total === 1 ? 'y' : 'ies'}`} />
      <PanelBody className="text-table">
        <p className="text-small text-ink-2 mb-3">{lead}</p>
        <div className="flex flex-wrap gap-6 mb-4">
          {STATUSES.filter((s) => counts[s]).map((s) => (
            <div key={s}>
              <div className="font-semibold text-[26px] leading-none">{fmt(counts[s])}</div>
              <div className="text-small text-ink-2">{label(s)}</div>
            </div>
          ))}
          {!total ? <p className="text-ink-2">None yet.</p> : null}
        </div>
        {!q.data.namesVisible ? (
          <InfoNotice>Statuses only: people and amounts are shown to admins.</InfoNotice>
        ) : entries.length ? (
          <table className="w-full">
            <thead>
              <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                <th className="py-2 pr-4">Person</th>
                <th className="py-2 pr-4">Kind</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Amount</th>
                <th className="py-2">Since</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-line">
                  <td className="py-[7px] pr-4">
                    {e.person.name}
                    <div className="text-small text-ink-2">{e.person.email ?? ''}</div>
                  </td>
                  <td className="py-[7px] pr-4">{label(e.kind)}</td>
                  <td className="py-[7px] pr-4">{label(e.status)}</td>
                  <td className="py-[7px] pr-4 tabular">{money(e.amountCents)}</td>
                  <td className="py-[7px]">
                    {new Date(e.statusChangedAt).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <p className="text-small text-ink-2 mt-3">
          Statuses change on the{' '}
          {see('payments') ? (
            <Link className="underline" to={`/payments?eventId=${detail.event.id}`}>
              Payments
            </Link>
          ) : (
            'Payments'
          )}{' '}
          page. Banking details never come to the platform.
        </p>
      </PanelBody>
    </Panel>
  );
}

export function ReimbursementsPanel(props: PanelProps) {
  return (
    <LedgerPanel
      {...props}
      kinds={['reimbursement']}
      title="Reimbursements"
      lead="Funded travellers get an entry when the event is closed (Attendance tab). Follow each one through York's steps below."
    />
  );
}

export function HonorariaPanel(props: PanelProps) {
  return (
    <LedgerPanel
      {...props}
      kinds={['honorarium', 'gift_card', 'prize']}
      title="Honoraria, prizes and gift cards"
      lead="Speaker honoraria, competition prizes and activity gift cards recorded for this event."
    />
  );
}
